import React, { useEffect, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import { FormikRadioGroup } from './FormikRadioGroup';
import { Box } from '@material-ui/core';
import type { FormField } from '../interfaces';
import { findCamundaValidation, getLocalizedLabel } from '../utils';
import { FormikNumericFormat } from './FormikNumericFormat';
import classNames from 'classnames';
import { getFormFieldStyles } from '../formFieldStyles';

export const IdeBudsjettertSelector = ({
    isRequired,
    isReadOnly,
    field,
    fields,
    locale,
}: {
    isRequired?: boolean;
    isReadOnly?: boolean;
    field?: FormField;
    fields?: Array<FormField>;
    locale: string;
}) => {
    const classes = getFormFieldStyles();
    const { setFieldValue } = useFormikContext();
    const [budsjettertFormikField] = useField(field?.id as string);
    const [budsjettnummerFormikField, budsjettnummerFormikMeta] =
        useField('budsjettnummer');

    const budsjettNummerField = fields?.find(
        (f) => f.id === budsjettnummerFormikField?.name,
    );

    const [showConditionalField, setShowConditionalField] = useState(undefined);

    const budsjettNummerFieldIsRequired = findCamundaValidation(
        'required',
        budsjettNummerField?.validation,
    );

    const budsjettNummerFieldIsReadOnly = findCamundaValidation(
        'readonly',
        budsjettNummerField?.validation,
    );

    useEffect(() => {
        if (
            budsjettertFormikField?.value === 'false' ||
            showConditionalField === false
        ) {
            if (!budsjettNummerField?.value && budsjettNummerFieldIsRequired) {
                setFieldValue(budsjettNummerField?.id as string, 0);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [budsjettertFormikField?.value, showConditionalField]);

    return (
        <Box className={classes.container}>
            <FormikRadioGroup
                {...budsjettertFormikField}
                id={field?.id}
                label={getLocalizedLabel(
                    `project.formFields.${field?.id}`,
                    field?.label,
                    field?.id,
                )}
                isRequired={isRequired}
                isReadOnly={isReadOnly}
                callback={setShowConditionalField}
                locale={locale}
            />
            {showConditionalField ||
            budsjettertFormikField?.value === 'true' ||
            (budsjettnummerFormikMeta.touched &&
                Boolean(budsjettnummerFormikMeta.error)) ? (
                <>
                    <Box paddingTop="15px">
                        <FormikNumericFormat
                            thousandSeparator={' '}
                            decimalSeparator=","
                            variant="outlined"
                            className={classNames({
                                [classes.editableField]: !isReadOnly,
                                [classes.readOnlyField]: isReadOnly,
                            })}
                            id={budsjettNummerField?.id}
                            isRequired={
                                budsjettNummerFieldIsRequired &&
                                (showConditionalField ||
                                    budsjettertFormikField?.value === 'true')
                            }
                            isReadOnly={
                                isReadOnly || budsjettNummerFieldIsReadOnly
                            }
                            label={getLocalizedLabel(
                                `project.formFields.budsjettnummer`,
                                budsjettNummerField?.label,
                                budsjettNummerField?.id,
                            )}
                            defaultValue={
                                budsjettNummerField?.value ||
                                budsjettNummerField?.defaultValue
                            }
                            helperText={
                                budsjettnummerFormikMeta.touched
                                    ? budsjettnummerFormikMeta.error
                                    : ''
                            }
                            error={
                                budsjettnummerFormikMeta.touched &&
                                Boolean(budsjettnummerFormikMeta.error)
                            }
                        />
                    </Box>
                </>
            ) : null}
        </Box>
    );
};
